import React from 'react';

import { useAppSelector } from '@/store';

import type { ContractService } from './api/contract';
import { contractService } from './api/service';

/** The last status answer per brand (true: the declaration is signed), kept for the session: the
 *  Settings index row and the section's title both say "secured" from it, so a return to either
 *  paints the known label at once. */
const securedByBrand = new Map<string, boolean>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Records a status answer for a brand: the Secure your brand section calls it with its own newest
 *  read, so the section's title follows the status its panel shows, with no read of its own. */
export const rememberBrandSecured = (brandId: string, secured: boolean): void => {
  if (securedByBrand.get(brandId) === secured) return;
  securedByBrand.set(brandId, secured);
  listeners.forEach((listener) => listener());
};

/**
 * Whether the displayed brand's License Holder Declaration is signed (spark-esign answers `secured`),
 * for the Secure your brand labels. With `read`, it reads the status itself once per brand (the
 * Settings index, while the `secure-brand` flag is on); without, it only follows what another read
 * recorded (the section's title, fed by the section's own read). A read that fails keeps the last
 * answer: with none, the brand counts as not secured, so the label stays "Secure your brand".
 */
export const useBrandSecured = (read: boolean, service: ContractService = contractService): boolean => {
  const brandId = useAppSelector((s) => s.casinoInstance.displayBrand?.id ?? null);
  const secured = React.useSyncExternalStore(subscribe, () =>
    brandId === null ? false : securedByBrand.get(brandId) === true,
  );

  // Syncs with spark-esign: one status read per brand. The answer is stored by brand, so one that
  // lands after a brand switch only updates that brand's entry.
  React.useEffect(() => {
    if (!read || brandId === null) return;
    service.fetchStatus(brandId).then(
      (status) => rememberBrandSecured(brandId, status.status === 'secured'),
      () => {
        // A failed read keeps whatever answer is held for this brand.
      },
    );
  }, [read, brandId, service]);

  return secured;
};

export default useBrandSecured;
